import {
  Button,
  ChoiceChips,
  Dialog,
  FormSection,
  Input,
  MAX_DECIMAL_LENGTH,
  Select,
  Skeleton,
  Textarea,
  formatNumber,
  formatSignedNumber,
} from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { ArrowLeftRight, Boxes, StickyNote, Warehouse } from 'lucide-react';
import type { ReactNode } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import styles from './AdjustStockSheet.module.css';
import { MAX_MOVEMENT_NOTE_LENGTH, defaultDepotOf, onHandAt } from './inventory.ts';
import type { StockRow } from './StockMatrix.tsx';
import {
  ADJUSTMENT_REASONS,
  evaluateEntry,
  hasValidUnitPrice,
  reviewAdjustment,
  takesUnitPrice,
  type AdjustmentPlan,
  type AdjustmentReason,
  type EntryOutcome,
} from './stock-adjustment.ts';
import { useRecordStock } from './useRecordStock.ts';
import { useStockMatrix } from './useStockMatrix.ts';
import { useTenantDetail } from '../catalog/useCatalogQueries.ts';
import { LoadFailed } from '../LoadFailed.tsx';
import { usePageContext } from '../usePageContext.ts';

type AdjustmentForm = {
  reason: AdjustmentReason;
  locationId: string;
  unitPrice: string;
  note: string;
  quantities: string[];
};

export type AdjustStockSheetProps = {
  api: TypedClient;
  subtitle: string;
  rows: readonly StockRow[];
  onClose: () => void;
};

export const AdjustStockSheet = ({ api, subtitle, rows, onClose }: AdjustStockSheetProps) => {
  const { t, locale, notify, describeError } = usePageContext();
  const stock = useStockMatrix(
    api,
    rows.map((row) => row.variantId),
  );
  const form = useForm<AdjustmentForm>({
    defaultValues: {
      reason: ADJUSTMENT_REASONS[0],
      locationId: '',
      unitPrice: rows[0]?.price ?? '',
      note: '',
      quantities: rows.map(() => ''),
    },
  });
  const [reason, chosenDepot, unitPrice, note, quantities] = useWatch({
    control: form.control,
    name: ['reason', 'locationId', 'unitPrice', 'note', 'quantities'],
  });
  const currency = useTenantDetail(api).data?.currency;
  const locationId = chosenDepot === '' ? defaultDepotOf(stock.locations) : chosenDepot;
  const plan: AdjustmentPlan = { reason, locationId, unitPrice, note };
  const entries = rows.map((row, position) => ({
    variantId: row.variantId,
    onHand: onHandAt(stock.levels, row.variantId, locationId),
    text: quantities[position] ?? '',
  }));
  const review = reviewAdjustment(plan, entries);

  const submit = useRecordStock(api, (recorded) => {
    notify(t('adjustStock.recorded', { count: recorded }));
    onClose();
  });

  const quantityLabel = t('adjustStock.quantity');

  const afterText = (outcome: EntryOutcome): ReactNode => {
    if (outcome.kind === 'empty') return '—';
    if (outcome.kind === 'invalid') return t('adjustStock.invalid');
    if (outcome.kind === 'negative')
      return t('adjustStock.negative', { after: formatNumber(outcome.after, locale) });
    return (
      <span className={styles['after']}>
        <strong>{formatNumber(outcome.after, locale)}</strong>{' '}
        <span className={styles['delta']} data-sign={Math.sign(outcome.change)}>
          {formatSignedNumber(outcome.change, locale)}
        </span>
      </span>
    );
  };

  const body = () => {
    if (stock.isError) return <LoadFailed description={describeError(stock.error)} onRetry={stock.refetch} />;
    if (stock.isPending) return <Skeleton height="160px" />;
    return (
      <>
        <FormSection
          variant="plain"
          title={t('adjustStock.reason')}
          description={t('adjustStock.operationHint')}
          icon={<ArrowLeftRight size={18} />}
        >
          <ChoiceChips
            legend={t('adjustStock.reason')}
            hideLegend
            locale={locale}
            value={reason}
            helperText={t(`adjustStock.reasonHelp_${reason}`)}
            choices={ADJUSTMENT_REASONS.map((value) => ({ value, label: t(`adjustStock.reason_${value}`) }))}
            onChange={(value) => {
              form.setValue('reason', value);
            }}
          />
          {takesUnitPrice(reason) ? (
            <Input
              label={t('adjustStock.unitPrice')}
              required
              inputMode="decimal"
              dir="ltr"
              suffix={currency}
              maxLength={MAX_DECIMAL_LENGTH}
              errorText={hasValidUnitPrice(plan) ? undefined : t('adjustStock.unitPriceInvalid')}
              {...form.register('unitPrice')}
            />
          ) : null}
        </FormSection>
        {stock.locations.length > 1 ? (
          <FormSection
            variant="plain"
            title={t('inventory.depot')}
            description={t('adjustStock.depotHint')}
            icon={<Warehouse size={18} />}
          >
            <Select
              label={t('inventory.depot')}
              hideLabel
              value={locationId}
              options={stock.locations.map((location) => ({ value: location.id, label: location.name }))}
              onChange={(event) => {
                form.setValue('locationId', event.target.value);
              }}
            />
          </FormSection>
        ) : null}
        <FormSection
          variant="plain"
          title={t('adjustStock.lines')}
          description={t('adjustStock.linesHint')}
          icon={<Boxes size={18} />}
        >
          <ul className={styles['lines']} aria-label={t('adjustStock.lines')}>
            {rows.map((row, position) => {
              const entry = entries[position];
              const outcome = entry === undefined ? ({ kind: 'empty' } as const) : evaluateEntry(plan, entry);
              const afterId = `adjust-after-${row.variantId}`;
              const refused = outcome.kind === 'invalid' || outcome.kind === 'negative';
              return (
                <li key={row.variantId} className={styles['line']}>
                  <span className={styles['variant']}>
                    <strong>{row.label}</strong>
                    {row.detail === undefined ? null : <span dir="ltr">{row.detail}</span>}
                  </span>
                  <span className={styles['figure']}>
                    <span className={styles['caption']}>{t('inventory.onHand')}</span>
                    {formatNumber(entry?.onHand ?? 0, locale)}
                  </span>
                  <Input
                    label={`${quantityLabel} · ${row.label}`}
                    hideLabel
                    className={styles['quantity']}
                    inputMode="numeric"
                    dir="ltr"
                    autoComplete="off"
                    placeholder="0"
                    aria-invalid={refused ? true : undefined}
                    aria-describedby={afterId}
                    {...form.register(`quantities.${String(position)}` as `quantities.${number}`)}
                  />
                  <span
                    id={afterId}
                    className={[styles['figure'], refused ? styles['refused'] : ''].join(' ')}
                    aria-live="polite"
                  >
                    <span className={styles['caption']}>{t('adjustStock.after')}</span> {afterText(outcome)}
                  </span>
                </li>
              );
            })}
          </ul>
        </FormSection>
        <FormSection
          variant="plain"
          title={t('inventory.note')}
          description={t('adjustStock.noteHint')}
          icon={<StickyNote size={18} />}
        >
          <Textarea
            label={t('inventory.note')}
            hideLabel
            placeholder={t('adjustStock.notePlaceholder')}
            maxLength={MAX_MOVEMENT_NOTE_LENGTH}
            rows={2}
            {...form.register('note')}
          />
        </FormSection>
        {submit.error === null ? null : (
          <p role="alert" className={styles['error']}>
            {describeError(submit.error) ?? t('errors.UNKNOWN')}
          </p>
        )}
      </>
    );
  };

  return (
    <Dialog
      open
      variant="side"
      title={t('adjustStock.title')}
      subtitle={subtitle}
      closeLabel={t('common.close')}
      onClose={onClose}
      footer={
        <>
          <span className={styles['summary']} role="status">
            {t('adjustStock.summary', {
              count: review.lines.length,
              change: formatSignedNumber(review.totalChange, locale),
            })}
          </span>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={review.blocked || review.lines.length === 0 || submit.isPending}
            onClick={() => {
              submit.mutate(review.lines);
            }}
          >
            {submit.isPending ? t('common.saving') : t('adjustStock.confirm')}
          </Button>
        </>
      }
    >
      <div className={styles['body']}>{body()}</div>
    </Dialog>
  );
};
